/**
 * @fileoverview Provides AST-related utility functions for analyzing TypeScript/JavaScript code, including methods for extracting code blocks, statements, references, and declarations. It uses tree-sitter for parsing and supports different node types and language parsing.
 */

import { readFileSync } from 'fs'
import { Parser } from './tree-sitter'
import TypeScript from 'tree-sitter-typescript'
import { dedent } from '@iterator/shared-common/src/dedent'

// TODO: add other languages from https://github.com/tree-sitter?q=&type=all&language=javascript&sort=

export type Block = {
  start: number
  end: number
  before: string[]
  current: string[]
  after: string[]
  text: string
  indentedText: string
}

export type Definition = {
  name: string
  line: number
}

export type Reference = {
  line: number
  definitionName: string
}

export type Statement = {
  text: string
  startLine: number
  endLine: number
}

/**
 * Get the best fit statements or block for the given line number.
 */
export function bestFitStatementsOrBlock(
  fileContents: string,
  options: { lineNumber: number; maxCharacters: number },
  language = TypeScript.typescript,
): Block | undefined {
  const block = getBlock(fileContents, options.lineNumber, language)
  if (block.text.length < options.maxCharacters) {
    return block
  }

  const statements = getBestFitStatements(fileContents, options, language)
  if (statements.length === 0) {
    return undefined
  }
  return statementsToBlock(fileContents, statements, options.lineNumber)
}

/**
 * Convert a list of statements to a block. The statement containing the line number will be
 * the current line. The before and after lines will be the statements before and after the current
 * line.
 * The block text will always include the entire line(s) of the statements.
 */
export function statementsToBlock(
  fileContents: string,
  statements: Statement[],
  lineNumber: number,
): Block {
  const fileLines = fileContents.split('\n')
  const currentStatement = statements.find(
    (s) => s.startLine <= lineNumber && s.endLine >= lineNumber,
  )
  if (!currentStatement) {
    throw new Error(`No statement found for line number ${lineNumber}`)
  }

  const beforeStatements = statements.filter((s) => s.endLine < currentStatement.startLine)
  const afterStatements = statements.filter((s) => s.startLine > currentStatement.endLine)

  const emptyLinesBetweenStatements = (
    thisStatement: Statement | undefined,
    nextStatement: Statement | undefined,
  ) => {
    const lines: string[] = []
    if (nextStatement && thisStatement && thisStatement.endLine < nextStatement.startLine - 1) {
      const numEmptyLinesToAdd = nextStatement.startLine - thisStatement.endLine - 1
      for (let i = 0; i < numEmptyLinesToAdd; i++) {
        lines.push('')
      }
    }
    return lines
  }
  const toLinesWithEmptyLines = (statements: Statement[]) => {
    const lines: string[] = []
    for (let i = 0; i < statements.length; i++) {
      const thisStatement = statements[i]!
      const nextStatement = statements[i + 1]
      lines.push(...fileLines.slice(thisStatement.startLine - 1, thisStatement.endLine))
      lines.push(...emptyLinesBetweenStatements(thisStatement, nextStatement))
    }
    return lines
  }
  const beforeLines = toLinesWithEmptyLines(beforeStatements)
  beforeLines.push(
    ...emptyLinesBetweenStatements(beforeStatements[beforeStatements.length - 1], currentStatement),
  )
  const currentLines = fileLines.slice(currentStatement.startLine - 1, currentStatement.endLine)
  const afterLines = toLinesWithEmptyLines(afterStatements)
  afterLines.unshift(...emptyLinesBetweenStatements(currentStatement, afterStatements[0]))

  const indentedText = [...beforeLines, ...currentLines, ...afterLines].join('\n')
  return {
    start: statements[0]!.startLine,
    end: statements[statements.length - 1]!.endLine,
    before: beforeLines,
    current: currentLines,
    after: afterLines,
    text: dedent(indentedText),
    indentedText,
  }
}

/**
 * Get the statement on the current line and add surrounding statements until we have maxCharacters.
 * Favor adding statements after the current line.
 * Leading and trailing whitespace is not included in the character count.
 * Does not go up the tree to include parent
 * If the line is more than maxCharacters, no statements will be returned.
 * Statements may not include the entire line. If the entire line is needed, use statementsToBlock.
 */
export function getBestFitStatements(
  fileContents: string,
  options: { lineNumber: number; maxCharacters: number },
  language = TypeScript.typescript,
): Statement[] {
  const parser = new Parser()
  parser.setLanguage(language)
  const tree = parser.parse(fileContents)
  const lines = fileContents.split('\n')

  const statement = statementForPosition(tree, {
    row: options.lineNumber - 1,
    lineLength: lines[options.lineNumber - 1]!.length,
  })
  const resultNodes: Parser.SyntaxNode[] = []
  let characterBudget = options.maxCharacters

  const statementText = dedentedTextFromSyntaxNode(statement)
  if (statementText.length < characterBudget) {
    resultNodes.push(statement)
    characterBudget -= statementText.length
  } else {
    return []
  }

  const previousQueue: Parser.SyntaxNode[] = []
  const nextQueue: Parser.SyntaxNode[] = []
  getSiblingStatements(statement).forEach((sibling) => {
    if (sibling.startPosition.row < statement.startPosition.row) {
      previousQueue.push(sibling)
    } else {
      nextQueue.push(sibling)
    }
  })
  previousQueue.reverse()

  const addFromNextQueue = (): boolean => {
    if (
      nextQueue.length > 0 &&
      dedentedTextFromSyntaxNode(nextQueue[0]!).length < characterBudget
    ) {
      const nextStatement = nextQueue.shift()!
      resultNodes.push(nextStatement)
      characterBudget -= dedentedTextFromSyntaxNode(nextStatement).length
      return true
    }
    return false
  }

  const addFromPreviousQueue = () => {
    if (
      previousQueue.length > 0 &&
      dedentedTextFromSyntaxNode(previousQueue[0]!).length < characterBudget
    ) {
      resultNodes.unshift(previousQueue.shift()!)
      characterBudget -= dedentedTextFromSyntaxNode(previousQueue[0]!).length
      return true
    }
    return false
  }

  while (characterBudget > 0) {
    const didAdd = [addFromNextQueue, addFromPreviousQueue]
      .map((add) => add())
      .some((didAdd) => didAdd)
    if (!didAdd) {
      break
    }
  }

  return resultNodes.map((node) => ({
    text: dedentedTextFromSyntaxNode(node),
    startLine: node.startPosition.row + 1,
    endLine: node.endPosition.row + 1,
  }))
}

function dedentedTextFromSyntaxNode(node: Parser.SyntaxNode): string {
  if (!node?.text) {
    return ''
  }
  const lines = node.text.split('\n')
  if (lines.length === 1) {
    return lines[0]!.trim()
  }
  return lines[0]!.trim() + '\n' + dedent(lines.slice(1).join('\n'))
}

/**
 * Get the block of code that contains the given line number and have it best fit the line.
 * 'Best fit' means if this is the opening or closing line to a block, we will choose the smaller option.
 */
export function getBlock(
  fileContents: string,
  lineNumber: number,
  language = TypeScript.typescript,
): Block {
  const parser = new Parser()
  parser.setLanguage(language)
  const tree = parser.parse(fileContents)
  const lines = fileContents.split('\n')

  const containingBlock = bestFitBlock(tree, {
    row: lineNumber - 1,
    lineLength: lines[lineNumber - 1]!.length,
  })

  const indentedText = lines
    .slice(containingBlock.startPosition.row, containingBlock.endPosition.row + 1)
    .join('\n')
  return {
    start: containingBlock.startPosition.row + 1,
    end: containingBlock.endPosition.row + 1,
    before: lines.slice(containingBlock.startPosition.row, lineNumber - 1),
    current: [lines[lineNumber - 1]!],
    after: lines.slice(lineNumber, containingBlock.endPosition.row + 1),
    text: dedent(indentedText),
    indentedText,
  }
}

/**
 * Get a block of code that contains the given line and has at least minLines lines (or the entire file
 * if there aren't enough lines). Used for grabbing surrounding block for context.
 * Never return the program node (aka the whole file).
 */
export function getBlockOfMinSize(
  fileContents: string,
  lineNumber: number,
  minLines = 50,
  language = TypeScript.typescript,
): Block {
  const parser = new Parser()
  parser.setLanguage(language)
  const tree = parser.parse(fileContents)
  const lines = fileContents.split('\n')

  let block = bestFitBlock(tree, {
    row: lineNumber - 1,
    lineLength: lines[lineNumber - 1]!.length,
  })

  while (blockSize(block) < minLines && block.parent && block.parent.type !== 'program') {
    block = block.parent
  }

  const indentedText = lines.slice(block.startPosition.row, block.endPosition.row + 1).join('\n')
  return {
    start: block.startPosition.row + 1,
    end: block.endPosition.row + 1,
    before: lines.slice(block.startPosition.row, lineNumber - 1),
    current: lines.slice(lineNumber - 1, lineNumber),
    after: lines.slice(lineNumber, block.endPosition.row + 1),
    text: dedent(indentedText),
    indentedText,
  }
}

/**
 * Get all the references in the file to the definitions on the given line. Note this is just
 * a string match so if there are variables with a matching name elsewhere in the file, they will
 * be included.
 */
export function getReferences(
  fileContents: string,
  lineNumber: number,
  language = TypeScript.typescript,
): { references: Reference[] } {
  const parser = new Parser()
  parser.setLanguage(language)
  const tree = parser.parse(fileContents)
  const lines = fileContents.split('\n')

  // Find all definitions in the target line
  const lineNodes: any[] = []
  for (let column = 0; column < lines[lineNumber - 1]!.length; column++) {
    const point = { row: lineNumber - 1, column }
    const node = tree.rootNode.descendantForPosition(point)
    if (!lineNodes.some((n) => n.id === node.id)) {
      lineNodes.push(node)
    }
  }

  // Find definitions in all nodes of the target line
  const definitions: Set<string> = new Set()
  const references: { line: number; text: string; definitionName: string }[] = []

  for (const node of lineNodes) {
    let currentNode = node
    while (currentNode && currentNode.startPosition.row === lineNumber - 1) {
      if (currentNode.type.endsWith('identifier')) {
        definitions.add(currentNode.text)
      }
      currentNode = currentNode.parent!
    }
  }

  // Find all references to the defined identifiers
  function findReferences(node: Parser.SyntaxNode) {
    if (node.type === 'identifier' && definitions.has(node.text)) {
      const lineNum = node.startPosition.row + 1
      if (lineNum !== lineNumber) {
        // Don't include references from the original line
        references.push({
          line: lineNum,
          text: lines[node.startPosition.row]!,
          definitionName: node.text,
        })
      }
    }
    for (const child of node.children || []) {
      findReferences(child)
    }
  }

  if (definitions.size > 0) {
    findReferences(tree.rootNode)
  }

  return { references }
}

function bestFitBlock(tree: Parser.Tree, line: { row: number; lineLength: number }) {
  const blockAtStartOfLine = containingBlockForPosition(tree, { row: line.row, column: 0 })
  const blockAtEndOfLine = containingBlockForPosition(tree, {
    row: line.row,
    column: line.lineLength,
  })

  if (blockSize(blockAtEndOfLine) < blockSize(blockAtStartOfLine)) {
    return blockAtEndOfLine
  }

  return blockAtStartOfLine
}

function containingBlockForPosition(tree: Parser.Tree, point: { row: number; column: number }) {
  const node = tree.rootNode.descendantForPosition(point)

  let containingBlock = node
  while (
    containingBlock &&
    // Unit tested
    ![
      'function_declaration',
      'if_statement',
      // No unit tests for these
      'method_definition',
      'class_declaration',
      'for_statement',
      'while_statement',
      'switch_statement',
      'try_statement',
      'catch_clause',
      'else_clause',
      'arrow_function',
      'block',
    ].includes(containingBlock.type) &&
    containingBlock.parent
  ) {
    containingBlock = containingBlock.parent
  }

  return containingBlock
}

function blockSize(block: Parser.SyntaxNode) {
  return block.endPosition.row - block.startPosition.row
}

export function statementForPosition(
  tree: Parser.Tree,
  point: { row: number; lineLength: number },
): Parser.SyntaxNode {
  let currentNode = tree.rootNode.descendantForPosition({
    row: point.row,
    column: point.lineLength / 2,
  })
  while (currentNode && !isStatementNode(currentNode) && currentNode.parent) {
    currentNode = currentNode.parent
  }
  return currentNode!
}

// Helper function to check if a node type is a statement
function isStatementNode(node: Parser.SyntaxNode): boolean {
  return [
    'expression_statement',
    'variable_declaration',
    'function_declaration',
    'lexical_declaration',
    'return_statement',
    'if_statement',
    'for_statement',
    'while_statement',
    'switch_statement',
    'throw_statement',
    'try_statement',
    'break_statement',
    'continue_statement',
    'formal_parameters',
    // Add other statement types as necessary
  ].includes(node.type)
}

function getSiblingStatements(statement: Parser.SyntaxNode): Parser.SyntaxNode[] {
  const parent = statement.parent
  if (!parent) {
    return []
  }

  const siblings = parent.children.filter((child) => child !== statement)
  return siblings.filter((sibling) => isStatementNode(sibling))
}

/**
 * Find the declarations at the given positions. Will coallesce methods onto their classes if both are included
 */
export async function getDeclarationsFromFile(
  filePath: string,
  positions: { row: number; column: number }[],
  language = TypeScript.typescript,
): Promise<string[]> {
  const parser = new Parser()
  parser.setLanguage(language)
  const fileContent = readFileSync(filePath, 'utf8')
  const tree = parser.parse(fileContent)

  const declarationNodes = positions
    .map((position) => findDeclarationNodeAtPosition(tree, position))
    .filter((node) => node !== undefined)

  const methodNodes = declarationNodes.filter((node) => node && node.type === 'method_definition')
  const nonMethodNodes = declarationNodes.filter(
    (node) => node && node.type !== 'method_definition',
  )

  return nonMethodNodes.map((node) => {
    if (node?.type === 'class_declaration') {
      return getClassTextWithMethodImplementations(fileContent, node, methodNodes)
    }
    return fileContent.substring(node.startIndex, node.endIndex).trim()
  })
}

function findDeclarationNodeAtPosition(
  tree: Parser.Tree,
  position: { row: number; column: number },
) {
  const nodeAtPosition = tree.rootNode.descendantForPosition(position)
  let declarationNode = nodeAtPosition

  while (
    declarationNode &&
    ![
      'function_declaration',
      'method_definition',
      'arrow_function',
      'interface_declaration',
      'type_alias_declaration',
      'class_declaration',
      'lexical_declaration',
    ].includes(declarationNode.type) &&
    declarationNode.parent
  ) {
    declarationNode = declarationNode.parent
  }

  // Do not:
  // - return entire files as declaration
  // - return the class declaration of the current class (assume this will be provided by <surrounding_block>)
  if (!declarationNode || declarationNode.type === 'program') {
    return undefined
  }

  return declarationNode
}

/**
 * Finds the function, class, interface, or type declaration at the given position.
 * @returns the text of the declaration or an empty string if no declaration is found.
 */
export async function getFunctionTypeOrInterfaceDeclaration(
  filePath: string,
  position: { row: number; column: number },
  language = TypeScript.typescript,
): Promise<string | undefined> {
  const parser = new Parser()
  parser.setLanguage(language)
  const fileContent = readFileSync(filePath, 'utf8')
  const tree = parser.parse(fileContent)

  const nodeAtPosition = tree.rootNode.descendantForPosition(position)
  let declarationNode = nodeAtPosition
  while (
    declarationNode &&
    ![
      'function_declaration',
      'method_definition',
      'arrow_function',
      'interface_declaration',
      'type_alias_declaration',
      'class_declaration',
    ].includes(declarationNode.type) &&
    declarationNode.parent
  ) {
    declarationNode = declarationNode.parent
  }

  // Do not:
  // - return entire files as declaration
  // - return the class declaration of the current class (assume this will be provided by <surrounding_block>)
  if (!declarationNode || declarationNode.type === 'program') {
    return undefined
  }

  if (declarationNode.type === 'method_definition') {
    return getMethodText(fileContent, declarationNode)
  }
  if (declarationNode.type === 'class_declaration') {
    return getClassText(fileContent, declarationNode)
  }
  return fileContent.substring(declarationNode.startIndex, declarationNode.endIndex).trim()
}

/**
 * We want the text of the method and we want to include the name of the class the method is in.
 */
function getMethodText(fileContent: string, methodNode: Parser.SyntaxNode): string {
  let classNode = methodNode
  while (classNode && classNode.type !== 'class_declaration' && classNode.parent) {
    classNode = classNode.parent
  }

  if (!classNode || classNode.type !== 'class_declaration') {
    return fileContent.substring(methodNode.startIndex, methodNode.endIndex)
  }

  const classNameNode = classNode.children.find((child) => child.type === 'type_identifier')
  const className = classNameNode
    ? fileContent.substring(classNameNode.startIndex, classNameNode.endIndex)
    : ''

  const methodSignature = fileContent.substring(methodNode.startIndex, methodNode.endIndex)
  const methodBody = methodNode.children.find((child) => child.type === 'statement_block')
  const methodSignatureWithBody = methodBody
    ? fileContent.substring(methodNode.startIndex, methodNode.endIndex)
    : methodSignature

  return `class ${className} {\n  ${methodSignatureWithBody}\n}`
}

/**
 * Class nodes can be very verbose so this function exposes only the class name and public methods
 * and properties but replaces all the private text (eg method implementations) with '...'.
 */
function getClassText(fileContent: string, declarationNode: Parser.SyntaxNode): string {
  // Get the class name from the first identifier node
  const classNameNode = declarationNode.children.find((child) => child.type === 'type_identifier')
  const className = classNameNode
    ? fileContent.substring(classNameNode.startIndex, classNameNode.endIndex)
    : ''

  // Find the class body node which contains all members
  const classBodyNode = declarationNode.children.find((child) => child.type === 'class_body')
  if (!classBodyNode) {
    return className
  }

  // Collect public members
  const publicMembers: string[] = []
  for (const member of classBodyNode.children) {
    // Skip private members (those starting with #) and non-public members
    const text = fileContent.substring(member.startIndex, member.endIndex)
    if (text.includes('private') || text.includes('protected') || text.includes('#')) {
      continue
    }

    if (member.type === 'method_definition' || member.type === 'public_field_definition') {
      // For methods, only include the signature
      if (member.type === 'method_definition') {
        const methodBody = member.children.find((child) => child.type === 'statement_block')
        if (methodBody) {
          const beforeBody = fileContent.substring(member.startIndex, methodBody.startIndex)
          publicMembers.push(beforeBody.trim() + ' { ... }')
          continue
        }
      }

      publicMembers.push(text.trim())
    }
  }

  // Construct the simplified class text
  return `class ${className} {
  ${publicMembers.join('\n  ')}
}`
}

/**
 * Class nodes can be very verbose so this function exposes only the class name and methods
 * and properties but replaces all the irrelevant method implementations with '...'.
 *
 * This differs from getClassText because it will include the method implementations of the
 * provided method nodes.
 */
function getClassTextWithMethodImplementations(
  fileContent: string,
  declarationNode: Parser.SyntaxNode,
  methodNodes: Parser.SyntaxNode[],
): string {
  // Get the class name from the first identifier node
  const classNameNode = declarationNode.children.find((child) => child.type === 'type_identifier')
  const className = classNameNode
    ? fileContent.substring(classNameNode.startIndex, classNameNode.endIndex)
    : ''

  // Find the class body node which contains all members
  const classBodyNode = declarationNode.children.find((child) => child.type === 'class_body')
  if (!classBodyNode) {
    return className
  }

  // Create a set of method nodes to include implementations for
  const methodNodeSet = new Set(methodNodes)

  // Collect all members
  const members: string[] = []
  for (const member of classBodyNode.children) {
    const text = fileContent.substring(member.startIndex, member.endIndex)

    if (member.type === 'method_definition') {
      // For methods, include full implementation if in methodNodes, otherwise just signature
      if (methodNodeSet.has(member)) {
        // Include full implementation
        members.push(text.trim())
      } else {
        // Just include signature
        const methodBody = member.children.find((child) => child.type === 'statement_block')
        if (methodBody) {
          const beforeBody = fileContent.substring(member.startIndex, methodBody.startIndex)
          members.push(beforeBody.trim() + ' { ... }')
          continue
        }
      }
    } else if (
      member.type === 'public_field_definition' ||
      member.type === 'private_field_definition'
    ) {
      // Include all field definitions
      members.push(text.trim())
    }
  }

  // Construct the class text with selected method implementations
  return `class ${className} {
  ${members.join('\n  ')}
}`
}

export enum PositionCategory {
  MethodArguments = 'method_arguments',
  EmptyBlock = 'empty_block',
  Unknown = 'unknown',
}

export type CategorizedBlock =
  | { category: PositionCategory.Unknown }
  | {
      category: Exclude<PositionCategory, PositionCategory.Unknown>
      text: string
      startPosition: { row: number; column: number }
      endPosition: { row: number; column: number }
    }

export function categorizePosition(
  code: string,
  position: { row: number; column: number },
  language = TypeScript.typescript,
): CategorizedBlock {
  const parser = new Parser()
  parser.setLanguage(language)
  const tree = parser.parse(code)
  const lines = code.split('\n')
  const line = lines[position.row - 1]
  if (!line) {
    return { category: PositionCategory.Unknown }
  }

  // Find the node at the cursor position
  const point = { row: position.row - 1, column: position.column - 1 }
  const node = tree.rootNode.descendantForPosition(point)
  if (!node) {
    return { category: PositionCategory.Unknown }
  }

  // Check if we're in method arguments
  const methodNode = checkIfNodeIsDescendantOf(node, ['formal_parameters', 'arguments'])
  if (methodNode) {
    return {
      category: PositionCategory.MethodArguments,
      text: methodNode.text,
      startPosition: {
        row: methodNode.startPosition.row + 1,
        column: methodNode.startPosition.column + 1,
      },
      endPosition: {
        row: methodNode.endPosition.row + 1,
        column: methodNode.endPosition.column + 1,
      },
    }
  }

  // Check if we're in an empty block
  if (node.type === 'statement_block' && node.text.replace(/\s/g, '') === '{}') {
    return {
      category: PositionCategory.EmptyBlock,
      text: node.text,
      startPosition: { row: node.startPosition.row + 1, column: node.startPosition.column + 1 },
      endPosition: { row: node.endPosition.row + 1, column: node.endPosition.column + 1 },
    }
  }

  return { category: PositionCategory.Unknown }
}

function checkIfNodeIsDescendantOf(
  node: Parser.SyntaxNode,
  parentTypes: string[],
): Parser.SyntaxNode | undefined {
  let currentNode = node
  while (currentNode && currentNode.parent) {
    if (parentTypes.includes(currentNode.parent.type)) {
      return currentNode.parent
    }
    currentNode = currentNode.parent
  }
  return undefined
}
